/**
 * Labels page — generate and download printable marker label sheets.
 * Users can select markers to include and customize the PDF output.
 */

import React, { useState, useMemo } from 'react';
import { useBoxStore } from '../store/BoxContext';
import { getLabelsPdfUrl } from '../api/labels';
import type { PageFormat, LabelSize } from '../api/labels';
import { getArucoSvgUrl, getQrSvgUrl } from '../api/markers';

export function LabelsPage() {
  const { state, fetchBoxes } = useBoxStore();
  const [selectedMarkers, setSelectedMarkers] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<PageFormat>('a4');
  const [labelSize, setLabelSize] = useState<LabelSize>('medium');

  // Fetch boxes on mount
  React.useEffect(() => {
    fetchBoxes({ pageSize: 100 });
  }, [fetchBoxes]);

  const boxes = state.boxes;

  function toggleMarker(markerKey: string) {
    setSelectedMarkers((prev) => {
      const next = new Set(prev);
      if (next.has(markerKey)) {
        next.delete(markerKey);
      } else {
        next.add(markerKey);
      }
      return next;
    });
  }

  function selectAll() {
    const allKeys = boxes.map((b) => `${b.markerType}:${b.markerId}`);
    setSelectedMarkers(new Set(allKeys));
  }

  function selectNone() {
    setSelectedMarkers(new Set());
  }

  const pdfUrl = useMemo(() => {
    if (selectedMarkers.size === 0) return null;
    return getLabelsPdfUrl(Array.from(selectedMarkers), format, labelSize);
  }, [selectedMarkers, format, labelSize]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Print Labels</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Select markers to include in a printable PDF sheet.
        </p>
      </div>

      {/* Options */}
      <div className="flex flex-wrap gap-4">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Page Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as PageFormat)}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary"
          >
            <option value="a4">A4</option>
            <option value="letter">US Letter</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Label Size</label>
          <select
            value={labelSize}
            onChange={(e) => setLabelSize(e.target.value as LabelSize)}
            className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary"
          >
            <option value="small">Small (30mm)</option>
            <option value="medium">Medium (50mm)</option>
            <option value="large">Large (70mm)</option>
          </select>
        </div>
      </div>

      {/* Selection controls */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={selectAll}
          className="text-sm text-accent hover:text-accent-hover transition-colors"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={selectNone}
          className="text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          Select none
        </button>
        <span className="text-sm text-text-secondary ml-auto">{selectedMarkers.size} selected</span>
      </div>

      {/* Marker grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {boxes.map((box) => {
          const key = `${box.markerType}:${box.markerId}`;
          const isSelected = selectedMarkers.has(key);
          const svgUrl =
            box.markerType === 'aruco'
              ? getArucoSvgUrl(parseInt(box.markerId, 10), 128)
              : getQrSvgUrl(box.markerId, 128);

          return (
            <button
              key={box.id}
              type="button"
              onClick={() => toggleMarker(key)}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                isSelected
                  ? 'border-accent bg-accent/10'
                  : 'border-border bg-surface hover:border-text-secondary'
              }`}
            >
              <img
                src={svgUrl}
                alt={`${box.markerType} marker ${box.markerId}`}
                className="w-16 h-16"
              />
              <div className="text-xs text-center">
                <div className="font-medium text-text-primary truncate max-w-[100px]">
                  {box.name}
                </div>
                <div className="text-text-secondary">
                  {box.markerType === 'aruco' ? `#${box.markerId}` : `QR`}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {boxes.length === 0 && !state.loading && (
        <div className="text-center py-12 text-text-secondary">
          <p>No boxes found. Create boxes first to generate labels.</p>
        </div>
      )}

      {/* Download button */}
      <div className="sticky bottom-20 z-10">
        {pdfUrl ? (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full rounded-lg bg-accent py-3 text-center text-sm font-semibold text-white hover:bg-accent-hover transition-colors shadow-lg"
          >
            Download PDF ({selectedMarkers.size} label{selectedMarkers.size !== 1 ? 's' : ''})
          </a>
        ) : (
          <button
            type="button"
            disabled
            className="block w-full rounded-lg bg-accent/50 py-3 text-center text-sm font-semibold text-white cursor-not-allowed"
          >
            Select markers to download
          </button>
        )}
      </div>
    </div>
  );
}
